import { Component, OnInit, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subject, takeUntil } from 'rxjs';
import { ToastService, Toast } from '@shared/services/toast.service';
import { IconComponent } from '@shared/components/icon/icon.component';
import { TranslateModule } from '@ngx-translate/core';

@Component({
	selector: 'app-toast-container',
	imports: [CommonModule, IconComponent, TranslateModule],
	standalone: true,
	template: `
		<div class="weToastContainer">
			@for (toast of toasts; track trackByFn($index, toast)) {
				<div class="weToast" [ngClass]="getToastClasses(toast)">
					<div class="weToast__icon">
						<app-icon [name]="getIconName(toast.type)" [size]="20" />
					</div>
					<div class="weToast__content">
						@if (toast.title) {
							<div class="weToast__title">{{ toast.title }}</div>
						}
						<div class="weToast__message">{{ toast.message }}</div>
					</div>
					@if (toast.dismissible) {
						<button class="weToast__close" [attr.aria-label]="'COMMON.CLOSE' | translate" (click)="dismiss(toast.id)">
							<app-icon name="close" [size]="16" />
						</button>
					}
				</div>
			}
		</div>
	`,
	styleUrl: './toast.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ToastComponent implements OnInit, OnDestroy {
	private readonly toastService = inject(ToastService);
	private readonly cdr = inject(ChangeDetectorRef);

	toasts: Toast[] = [];
	dismissingToasts = new Set<string>();
	private readonly destroy$ = new Subject<void>();

	ngOnInit(): void {
		// Register animated dismiss callback
		this.toastService.onAnimatedDismiss = (id: string) => {
			this.dismiss(id);
		};

		this.toastService.toasts$.pipe(takeUntil(this.destroy$)).subscribe((toasts) => {
			this.toasts = toasts;
			this.cdr.markForCheck();
		});
	}

	ngOnDestroy(): void {
		// Clean up callback
		if (this.toastService.onAnimatedDismiss) {
			this.toastService.onAnimatedDismiss = undefined;
		}
		this.destroy$.next();
		this.destroy$.complete();
	}

	dismiss(id: string): void {
		// Add to dismissing set to trigger animation
		this.dismissingToasts.add(id);
		this.cdr.markForCheck();

		// Wait for animation to complete, then actually dismiss
		setTimeout(() => {
			this.toastService.dismiss(id);
			this.dismissingToasts.delete(id);
		}, 300); // Match the animation duration
	}

	getIconName(type: string): string {
		switch (type) {
			case 'success':
				return 'success';
			case 'error':
				return 'error';
			case 'warning':
				return 'warning';
			case 'info':
				return 'info';
			default:
				return 'info';
		}
	}

	getToastClasses(toast: Toast): Record<string, boolean> {
		const classes: Record<string, boolean> = {};
		classes[`weToast--${toast.type}`] = true;
		classes['weToast--dismissing'] = this.dismissingToasts.has(toast.id);
		return classes;
	}

	trackByFn(index: number, toast: Toast): string {
		return toast.id;
	}
}
